import psHttp from '@patsnap/common-ps-http';
import { DRAFTING_URI } from '@/constants/base-url';
import type {
    Project,
    ProjectListParams,
    ProjectListItem,
    CreateProjectRequest,
    SubmitProjectRequest,
    UpdateProjectRequest,
    UpdateProjectTitleRequest,
    DeleteProjectsRequest,
    ProjectResult,
    ProjectStatusResponse,
    ApiResponse,
    ProjectMutationData,
    DeleteProjectsData,
    ProjectResourceStats,
} from '@/types/project';
import type { DraftingModelValue } from '@/features/drafting/components/selectors/model-options';
import type { RejectionIssueStrategyEdits, RejectionTableData } from '@/types/project-event';

export interface ProjectModelSchemeResponse {
    enabled: boolean;
    selected_scheme_id: DraftingModelValue;
    effective_config_group_id: string | null;
}

export interface ProjectQaOnlyState {
    project_id: string;
    generation_mode: Project['generation_mode'];
    qa_only: boolean;
}

function resolveProjectExportFilename(contentDisposition?: string | null, fallbackFilename = 'project.zip'): string {
    if (!contentDisposition) return fallbackFilename;
    const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (filenameStarMatch) {
        try {
            return decodeURIComponent(filenameStarMatch[1]);
        } catch {
            return fallbackFilename;
        }
    }
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i)
        || contentDisposition.match(/filename=([^;]+)/i);
    return filenameMatch?.[1]?.trim() || fallbackFilename;
}

/**
 * 获取项目列表
 */
export async function getProjectListApi(params?: ProjectListParams) {
    const response = await psHttp<ApiResponse<{
        items: ProjectListItem[];
        total: number;
        page: number;
        page_size: number;
    }>>({
        url: `${DRAFTING_URI}/api/projects/list`,
        method: 'POST',
        data: {
            keyword: params?.keyword || null,
            status: params?.status || null,
            style: params?.style || null,
            folder_id: params?.folder_id || null,
            client_list: params?.client_list || null,
            ordering: params?.ordering || '-created_at',
            page: params?.page || 1,
            page_size: params?.page_size || 20,
        },
    });
    // 后端返回 BaseResponse 包装的数据，需要从 data 中提取
    // 将后端的 total 字段映射为前端期望的 count 字段
    return {
        items: response.data.data.items,
        count: response.data.data.total,
    };
}

/**
 * 获取最近处理过的项目
 */
export async function getRecentProjectsApi(params?: {
    limit?: number;
    work_type?: string; // 添加 work_type 参数
    client_list?: string[]; // 添加 client_list 参数
}) {
    const response = await psHttp<ApiResponse<{
        projects: ProjectListItem[];
        count: number;
    }>>({
        url: `${DRAFTING_URI}/api/projects/recent`,
        method: 'GET',
        params: {
            limit: params?.limit || 5,
            work_type: params?.work_type || undefined,
            client_list: params?.client_list && params.client_list.length > 0
                ? params.client_list.join(',')
                : undefined,
        },
    });
    // 后端返回 BaseResponse 包装的数据，需要从 data.projects 中提取数组
    return response.data.data.projects;
}

/**
 * 获取项目详情
 */
export async function getProjectDetailApi(projectId: string) {
    const response = await psHttp<ApiResponse<Project>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}/detail`,
        method: 'GET',
    });
    return response.data;
}

/**
 * 更新项目的“仅问答”状态。
 * 后端返回项目最新的模式字段，调用方将其合并到已有项目详情，避免并发状态漂移。
 */
export async function updateProjectQaOnlyApi(projectId: string, enabled: boolean) {
    const response = await psHttp<ApiResponse<ProjectQaOnlyState>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}/qa-only`,
        method: 'PATCH',
        data: { enabled },
    });
    return response.data;
}

/**
 * 创建项目（仅保存，不开始处理）
 */
export async function createProjectApi(data: CreateProjectRequest) {
    const response = await psHttp<ApiResponse<ProjectMutationData>>({
        url: `${DRAFTING_URI}/api/projects/`,
        method: 'POST',
        data,
    });
    return response.data;
}

/**
 * 提交项目并开始异步处理
 */
export async function submitProjectApi(data: SubmitProjectRequest) {
    const response = await psHttp<ApiResponse<ProjectMutationData>>({
        url: `${DRAFTING_URI}/api/projects/submit`,
        method: 'POST',
        data,
    });
    return response.data;
}

/**
 * 更新项目
 */
export async function updateProjectApi(projectId: string, data: UpdateProjectRequest) {
    const response = await psHttp<ApiResponse<ProjectMutationData>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}`,
        method: 'PUT',
        data,
    });
    return response.data;
}

/**
 * 更新项目标题
 */
export async function updateProjectTitleApi(projectId: string, data: UpdateProjectTitleRequest) {
    const response = await psHttp<ApiResponse<ProjectMutationData>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}/title`,
        method: 'PATCH',
        data,
    });
    return response.data;
}

/**
 * 导出项目全部文档和普通附件
 */
export async function exportProjectAllApi(projectId: string, fallbackFilename?: string) {
    const response = await psHttp({
        url: `${DRAFTING_URI}/api/projects/${projectId}/export-all`,
        method: 'POST',
        responseType: 'blob',
    });
    const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
    return {
        blob: response.data as Blob,
        filename: resolveProjectExportFilename(contentDisposition, fallbackFilename),
    };
}

/**
 * 批量删除项目
 */
export async function deleteProjectsApi(data: DeleteProjectsRequest) {
    const response = await psHttp<ApiResponse<DeleteProjectsData>>({
        url: `${DRAFTING_URI}/api/projects/`,
        method: 'DELETE',
        data,
    });
    return response.data;
}

/**
 * 获取项目生成结果
 */
export async function getProjectResultApi(projectId: string, version?: number) {
    const response = await psHttp<ApiResponse<ProjectResult>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}/result`,
        method: 'GET',
        params: version ? { version } : undefined,
    });
    return response.data;
}

/**
 * 获取项目状态
 */
export async function getProjectStatusApi(projectId: string) {
    const response = await psHttp<ApiResponse<ProjectStatusResponse>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}/status`,
        method: 'GET',
    });
    return response.data;
}

/**
 * 获取项目关联资源统计
 * 用于右侧功能面板的 badge 显示
 */
export async function getProjectResourceStatsApi(projectId: string) {
    const response = await psHttp<ApiResponse<ProjectResourceStats>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}/resource-stats`,
        method: 'GET',
    });
    return response.data.data;
}

/**
 * 获取项目数据详情
 */
export async function getProjectModelSchemeApi(projectId: string) {
    const response = await psHttp<ApiResponse<ProjectModelSchemeResponse>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}/model-scheme`,
        method: 'GET',
    });
    return response.data.data;
}

export async function updateProjectModelSchemeApi(projectId: string, modelSchemeId: DraftingModelValue) {
    const response = await psHttp<ApiResponse<ProjectModelSchemeResponse>>({
        url: `${DRAFTING_URI}/api/projects/${projectId}/model-scheme`,
        method: 'PUT',
        data: {
            model_scheme_id: modelSchemeId,
        },
    });
    return response.data.data;
}

export async function getProjectDataApi(dataId: string) {
    const response = await psHttp<ApiResponse<{
        id: string;
        project_id: string;
        relevance_key: string;
        data_type: string;
        data: any;
        description: string;
        created_at: string;
        updated_at: string;
        created_by: string;
    }>>({
        url: `${DRAFTING_URI}/api/projects/data/${dataId}`,
        method: 'GET',
    });
    return response.data.data;
}

/**
 * 更新项目数据
 */
export async function updateProjectDataApi(dataId: string, data: any, description?: string) {
    const response = await psHttp<ApiResponse<{
        id: string;
        project_id: string;
        relevance_key: string;
        data_type: string;
    }>>({
        url: `${DRAFTING_URI}/api/projects/data/${dataId}`,
        method: 'PUT',
        data: {
            data,
            description,
        },
    });
    return response.data.data;
}

export interface RejectionTableEditsPayload {
    base_revision: number;
    integration_updates?: {
        overall_strategy?: string;
    };
    issue_updates?: Array<{
        issue_id: string;
        updates: RejectionIssueStrategyEdits;
    }>;
}

export interface RejectionTableEditsResult {
    rejection_data_id: string;
    revision: number;
    integration_stale: boolean;
    changed_paths: string[];
    data: RejectionTableData;
}

export async function updateRejectionTableEditsApi(
    dataId: string,
    payload: RejectionTableEditsPayload,
): Promise<RejectionTableEditsResult> {
    const response = await psHttp<ApiResponse<RejectionTableEditsResult>>({
        url: `${DRAFTING_URI}/api/oar/rejection-tables/${dataId}/edits`,
        method: 'PATCH',
        data: payload,
    });
    return response.data.data;
}

/**
 * 项目所有权转移
 */
export async function transferProjectOwnershipApi(projectId: string) {
    try {
        const response = await psHttp<any>({
            url: `${DRAFTING_URI}/api/projects/transfer_ownership`,
            method: 'POST',
            data: {
                project_id: projectId,
            },
        });
        return response;
    } catch (error: any) {
        // 如果是400错误，不抛出异常，只是记录日志
        if (error?.response?.status === 400) {
            return { status: 400, ignored: true };
        }
        // 其他错误继续抛出
        throw error;
    }
}
